/**
 * DetalheDoAgente — tudo de um agente no período: série por dia (custo e invocações),
 * comparação entre VERSÕES DE PROMPT lado a lado, custo e tempo por etapa, loops por
 * projeto, erros por tipo, skills e tier/modelo.
 *
 * Fonte: GET /observability/agents/{nome} — tudo vem calculado do servidor sobre o
 * período inteiro (e o projeto do filtro), com execução excluída fora: série, quebras,
 * reforços e portão do agente e de cada versão de prompt, loops por projeto, erros por
 * tipo e modelos. GET /observability/agents só alimenta o seletor de agente.
 *
 * O cabeçalho tem uma área de AÇÕES do agente (`data-testid="acoes-do-agente"`): é ali
 * que entra, no futuro, "Sugerir melhoria com IA" — os dados que essa ação precisa
 * (versões de prompt, erros por tipo, loops, skills) já estão carregados neste componente.
 */
import { useMemo } from 'react';
import { C } from '@/theme/palette';
import { LoadingSpinner, QueryError } from '@/components/ui/QueryState';
import { useObsAgente, useObsAgentes } from '@/api/observability';
import { dataHora } from '@/lib/format';
import {
  SEM_PROJETO, custo, decimal, diaCurto, duracao, idCurto, inteiro, pct, preencherDias, rotulo,
} from './formato';
import {
  AvisoSemCusto, BarrasPorDia, Custo, Kpi, Nota, Titulo, Vazio, estilos,
} from './partes';

/** Tabela de uma quebra da API (`GrupoOut`). */
function TabelaDeGrupos({ grupos, rotuloDaChave, visiveis, formatarChave, testId }) {
  const s = estilos();
  if (!grupos || grupos.length === 0) return <Vazio>Sem dados no período.</Vazio>;
  return (
    <div style={{ overflowX: 'auto' }} data-testid={testId}>
      <table style={s.tabela}>
        <thead>
          <tr>
            <th style={s.th}>{rotuloDaChave}</th>
            <th style={s.thNum}>Invocações</th>
            <th style={s.thNum}>Custo total</th>
            <th style={s.thNum}>Custo médio</th>
            <th style={s.thNum}>Duração mediana</th>
            <th style={s.thNum}>Duração p90</th>
            <th style={s.thNum}>Taxa de erro</th>
          </tr>
        </thead>
        <tbody>
          {grupos.map((g) => (
            <tr key={g.chave}>
              <td style={{ ...s.td, wordBreak: 'break-word' }} title={g.chave}>
                {formatarChave ? formatarChave(g.chave) : g.chave}
              </td>
              <td style={s.tdNum}>{inteiro(g.invocacoes)}</td>
              <td style={s.tdNum}><Custo valor={g.custo_total_usd} visivel={visiveis} /></td>
              <td style={s.tdNum}><Custo valor={g.custo_medio_usd} visivel={visiveis} /></td>
              <td style={s.tdNum}>{duracao(g.duracao_mediana_ms)}</td>
              <td style={s.tdNum}>{duracao(g.duracao_p90_ms)}</td>
              <td style={{ ...s.tdNum, color: g.erros > 0 ? C.bad : C.text }}>
                {pct(g.taxa_de_erro_pct)} <span style={{ color: C.neutralMuted }}>({inteiro(g.erros)})</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Índice do menor valor (o "melhor") entre os grupos; só marca quando há disputa. */
function indiceDoMenor(grupos, campo) {
  const vals = grupos.map((g) => (g[campo] === null || g[campo] === undefined ? null : Number(g[campo])));
  const validos = vals.filter((v) => v != null);
  if (validos.length < 2) return -1;
  const min = Math.min(...validos);
  if (validos.every((v) => v === min)) return -1;
  return vals.indexOf(min);
}

/** Índice do maior valor (o "melhor" quando mais é melhor); só marca quando há disputa. */
function indiceDoMaior(grupos, campo) {
  const vals = grupos.map((g) => (g[campo] === null || g[campo] === undefined ? null : Number(g[campo])));
  const validos = vals.filter((v) => v != null);
  if (validos.length < 2) return -1;
  const max = Math.max(...validos);
  if (validos.every((v) => v === max)) return -1;
  return vals.indexOf(max);
}

/** Versões de prompt lado a lado: uma coluna por versão, uma linha por métrica. */
function VersoesDePrompt({ versoes, visiveis }) {
  const s = estilos();
  if (!versoes || versoes.length === 0) return <Vazio>Sem dados no período.</Vazio>;
  const metricas = [
    { rotulo: 'Invocações', render: (g) => inteiro(g.invocacoes) },
    { rotulo: 'Execuções', render: (g) => inteiro(g.execucoes) },
    {
      rotulo: 'Portão fechado na 1ª rodada', campo: 'portao_fechado_na_1a_rodada_pct', maiorEhMelhor: true,
      render: (g) => `${pct(g.portao_fechado_na_1a_rodada_pct)} (${inteiro(g.etapas)} etapa(s))`,
    },
    {
      rotulo: 'Reforços por etapa', campo: 'reforcos_por_etapa',
      render: (g) => `${decimal(g.reforcos_por_etapa)} (${inteiro(g.reforcos)} reforço(s))`,
    },
    ...(visiveis ? [
      { rotulo: 'Custo médio', campo: 'custo_medio_usd', render: (g) => <Custo valor={g.custo_medio_usd} /> },
      { rotulo: 'Custo total', render: (g) => <Custo valor={g.custo_total_usd} /> },
    ] : [
      { rotulo: 'Custo médio', render: () => <Custo visivel={false} /> },
    ]),
    { rotulo: 'Duração mediana', campo: 'duracao_mediana_ms', render: (g) => duracao(g.duracao_mediana_ms) },
    { rotulo: 'Duração p90', campo: 'duracao_p90_ms', render: (g) => duracao(g.duracao_p90_ms) },
    { rotulo: 'Taxa de erro', campo: 'taxa_de_erro_pct', render: (g) => `${pct(g.taxa_de_erro_pct)} (${inteiro(g.erros)})` },
  ];
  return (
    <div style={{ overflowX: 'auto' }} data-testid="versoes-de-prompt">
      <table style={s.tabela}>
        <thead>
          <tr>
            <th style={s.th}>Métrica</th>
            {versoes.map((v) => (
              <th key={v.chave} style={{ ...s.thNum, textTransform: 'none', letterSpacing: 0 }} title={v.chave}>
                {v.chave.startsWith('(') || v.chave.startsWith('hash:') ? v.chave : `versão ${idCurto(v.chave)}`}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {metricas.map((m) => {
            const melhor = !m.campo ? -1
              : m.maiorEhMelhor ? indiceDoMaior(versoes, m.campo) : indiceDoMenor(versoes, m.campo);
            return (
              <tr key={m.rotulo}>
                <td style={{ ...s.td, color: C.neutralDim }}>{m.rotulo}</td>
                {versoes.map((v, i) => (
                  <td key={v.chave} style={{ ...s.tdNum, color: i === melhor ? C.ok : C.text, fontWeight: i === melhor ? 600 : 400 }}>
                    {m.render(v)}{i === melhor ? <span aria-label="melhor valor"> ✓</span> : null}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function DetalheDoAgente({
  nome, filtro, nomeDoProjeto = (id) => id, aoTrocarAgente, aoAbrirExecucao, aoVerInvocacoes,
}) {
  const s = estilos();
  const periodo = { de: filtro?.de, ate: filtro?.ate, projectId: filtro?.projectId };
  const lista = useObsAgentes(periodo);
  const detalhe = useObsAgente(nome, periodo);

  const agentes = lista.data?.agentes || [];
  const d = detalhe.data;
  const visiveis = d?.custos_visiveis !== false;
  const serie = useMemo(() => preencherDias(d?.serie || []), [d]);
  const loops = d?.loops_por_projeto || [];
  const tiposDeErro = d?.erros_por_tipo || [];
  const modelos = d?.modelos || [];
  const doProjeto = filtro?.projectId ? ' neste projeto' : '';

  const seletor = (
    <label style={s.rotuloDeCampo}>
      Agente
      <select aria-label="Escolher agente" value={nome || ''} style={{ ...s.campo, minWidth: 240 }}
        onChange={(e) => aoTrocarAgente(e.target.value)}>
        <option value="">Selecione um agente…</option>
        {nome && !agentes.some((a) => a.agent_name === nome) ? <option value={nome}>{nome}</option> : null}
        {agentes.map((a) => (
          <option key={a.agent_name} value={a.agent_name}>
            {a.agent_name} — {inteiro(a.invocacoes)} invocação(ões)
          </option>
        ))}
      </select>
    </label>
  );

  if (!nome) {
    return (
      <div data-testid="obs-agente">
        {seletor}
        {lista.isLoading ? <LoadingSpinner label="Carregando agentes..." />
          : lista.isError ? <div style={{ marginTop: 14 }}><QueryError error={lista.error} onRetry={lista.refetch} /></div>
            : <Vazio>{agentes.length === 0
              ? 'Nenhum agente com invocação no período.'
              : 'Escolha um agente acima ou clique em um agente no ranking da Visão geral.'}</Vazio>}
      </div>
    );
  }

  return (
    <div data-testid="obs-agente">
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        {seletor}
        {/* Área de ações do agente — ponto de entrada de "Sugerir melhoria com IA" (futuro). */}
        <div data-testid="acoes-do-agente" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" style={s.botao} onClick={() => aoVerInvocacoes(nome)}>
            Ver invocações deste agente
          </button>
        </div>
      </div>

      {detalhe.isLoading ? <LoadingSpinner label="Carregando o agente..." />
        : detalhe.isError ? <QueryError error={detalhe.error} onRetry={detalhe.refetch} />
          : !d || (d.invocacoes || 0) === 0 ? (
            <Vazio>Nenhuma invocação de “{nome}” no período{doProjeto}.</Vazio>
          ) : (
            <>
              {!visiveis ? <AvisoSemCusto /> : null}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                <Kpi label="Invocações" value={inteiro(d.invocacoes)}
                  sub={`${inteiro(d.execucoes)} execução(ões) · ${inteiro(d.projetos)} projeto(s)`} />
                <Kpi label="Custo total (US$)" value={<Custo valor={d.custo_total_usd} visivel={visiveis} completo />}
                  sub={visiveis ? <>médio por invocação: <Custo valor={d.custo_medio_usd} /></> : null} />
                <Kpi label="Duração mediana" value={duracao(d.duracao_mediana_ms)} sub={`p90: ${duracao(d.duracao_p90_ms)}`} />
                <Kpi label="Taxa de erro" value={pct(d.taxa_de_erro_pct)} sub={`${inteiro(d.erros)} erro(s)`} />
                <Kpi testId="kpi-reforcos-do-agente" label="Reforços por etapa" value={decimal(d.reforcos_por_etapa)}
                  sub={`${inteiro(d.reforcos)} reforço(s) em ${inteiro(d.etapas)} etapa(s)`} />
                <Kpi testId="kpi-portao-do-agente" label="Portão fechado na 1ª rodada"
                  value={pct(d.portao_fechado_na_1a_rodada_pct)} sub={`${inteiro(d.etapas)} etapa(s) com resultado`} />
              </div>

              <Titulo lado="dias em UTC">Série diária</Titulo>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                {visiveis ? (
                  <BarrasPorDia testId="serie-custo" titulo="Custo por dia (US$)" pontos={serie}
                    valorDe={(p) => p.custo_total_usd} formatar={custo} cor={C.cyber} />
                ) : (
                  <div style={{ flex: '1 1 380px', background: C.blackElev, border: `1px solid ${C.blackBorder}`, borderRadius: 12, padding: '14px 16px' }}>
                    <div style={{ fontSize: 12, letterSpacing: '.04em', textTransform: 'uppercase', color: C.neutralMuted, marginBottom: 8 }}>
                      Custo por dia (US$)
                    </div>
                    <Custo visivel={false} completo />
                  </div>
                )}
                <BarrasPorDia testId="serie-invocacoes" titulo="Invocações por dia" pontos={serie}
                  valorDe={(p) => p.invocacoes} cor={C.digital} />
              </div>
              <details style={{ marginTop: 8 }}>
                <summary style={{ cursor: 'pointer', color: C.accent, fontSize: 13 }}>
                  Ver os números por dia ({inteiro((d.serie || []).length)} dia(s) com invocação)
                </summary>
                <div style={{ marginTop: 8 }}>
                  <TabelaDeGrupos grupos={d.serie} rotuloDaChave="Dia" visiveis={visiveis} formatarChave={diaCurto} />
                </div>
              </details>

              <Titulo lado={`${inteiro((d.por_versao_de_prompt || []).length)} versão(ões) no período`}>
                Versões de prompt — lado a lado
              </Titulo>
              <VersoesDePrompt versoes={d.por_versao_de_prompt} visiveis={visiveis} />
              <Nota testId="nota-versoes">
                ✓ marca o melhor valor da linha: o maior % de portão fechado na 1ª rodada e o menor
                custo, duração, erro e reforço. O portão de cada versão vem das etapas em que o agente
                rodou com aquele prompt (mesma execução e etapa).
                {d.resultados_sem_invocacao > 0 ? (
                  <> <strong>{inteiro(d.resultados_sem_invocacao)}</strong> resultado(s) de etapa não têm
                    invocação registrada para dizer a versão: contam no cartão do agente e em nenhuma coluna.</>
                ) : null}
              </Nota>

              <Titulo>Custo e tempo por etapa</Titulo>
              <TabelaDeGrupos testId="por-etapa" grupos={d.por_etapa} rotuloDaChave="Etapa" visiveis={visiveis} />

              <Titulo>Custo e tempo por projeto</Titulo>
              <TabelaDeGrupos testId="por-projeto" grupos={d.por_projeto} rotuloDaChave="Projeto" visiveis={visiveis}
                formatarChave={nomeDoProjeto} />

              <Titulo>Loops por projeto — reforços, reaberturas e rodadas</Titulo>
              {loops.length === 0 ? <Vazio>Sem invocações no período.</Vazio> : (
                <div style={{ overflowX: 'auto' }} data-testid="loops-por-projeto">
                  <table style={s.tabela}>
                    <thead>
                      <tr>
                        <th style={s.th}>Projeto</th>
                        <th style={s.thNum}>Execuções</th>
                        <th style={s.thNum}>Principais</th>
                        <th style={s.thNum}>Reforços</th>
                        <th style={s.thNum}>Reaberturas</th>
                        <th style={s.thNum}>Reparos do verify</th>
                        <th style={s.thNum}>Outras rodadas</th>
                        <th style={s.thNum}>Delegações</th>
                        <th style={s.thNum}>Maior rodada</th>
                        <th style={s.thNum}>Custo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loops.map((g) => (
                        <tr key={g.projeto}>
                          <td style={{ ...s.td, wordBreak: 'break-word' }} title={g.projeto}>
                            {g.projeto === SEM_PROJETO ? g.projeto : nomeDoProjeto(g.projeto)}
                          </td>
                          <td style={s.tdNum}>{inteiro(g.execucoes)}</td>
                          <td style={s.tdNum}>{inteiro(g.principais)}</td>
                          <td style={{ ...s.tdNum, color: g.reforcos > 0 ? C.warn : C.text }}>{inteiro(g.reforcos)}</td>
                          <td style={{ ...s.tdNum, color: g.reaberturas > 0 ? C.warn : C.text }}>{inteiro(g.reaberturas)}</td>
                          <td style={s.tdNum}>{inteiro(g.reparos_do_verify)}</td>
                          <td style={s.tdNum}>{inteiro(g.outras)}</td>
                          <td style={s.tdNum}>{inteiro(g.delegacoes)}</td>
                          <td style={s.tdNum}>{inteiro(g.maior_rodada)}</td>
                          <td style={s.tdNum}><Custo valor={g.custo_usd} visivel={visiveis} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <Nota>“Outras rodadas”: revisão, re-revisão, correção de blueprint e de estilo, cobertura e continuação.</Nota>

              <Titulo lado={`${inteiro(d.erros)} erro(s) no período`}>Erros por tipo</Titulo>
              {tiposDeErro.length === 0 ? <Vazio>Nenhum erro registrado no período.</Vazio> : (
                <div style={{ overflowX: 'auto' }} data-testid="erros-por-tipo">
                  <table style={s.tabela}>
                    <thead>
                      <tr>
                        <th style={s.th}>Tipo de erro</th>
                        <th style={s.thNum}>Ocorrências</th>
                        <th style={s.th}>Etapas</th>
                        <th style={s.th}>Mais recente</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tiposDeErro.map((g) => (
                        <tr key={g.tipo}>
                          <td style={{ ...s.td, color: C.bad, wordBreak: 'break-word' }}>{g.tipo}</td>
                          <td style={s.tdNum}>{inteiro(g.ocorrencias)}</td>
                          <td style={s.td}>{g.etapas?.length ? g.etapas.join(', ') : '—'}</td>
                          <td style={{ ...s.td, maxWidth: 520, overflowWrap: 'anywhere' }}>
                            <div style={{ color: C.neutralMuted, fontSize: 12 }}>
                              {dataHora(g.ultima_em)}
                              {g.execution_id ? (
                                <>{' · '}
                                  <button type="button" style={s.link} onClick={() => aoAbrirExecucao(g.execution_id)}>
                                    ver execução {idCurto(g.execution_id)}
                                  </button>
                                </>
                              ) : null}
                            </div>
                            {g.resumo || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <Titulo>Skills usadas</Titulo>
              <TabelaDeGrupos testId="por-skill" grupos={d.por_skill} rotuloDaChave="Skill" visiveis={visiveis} />
              <Nota>O custo de uma invocação entra em cada skill que ela carregava — a soma por skill não é o total do agente.</Nota>

              <Titulo>Tier e modelo</Titulo>
              <TabelaDeGrupos testId="por-tier" grupos={d.por_tier} rotuloDaChave="Tier" visiveis={visiveis} />
              {modelos.length > 0 ? (
                <div style={{ overflowX: 'auto', marginTop: 10 }} data-testid="modelos">
                  <table style={s.tabela}>
                    <thead>
                      <tr>
                        <th style={s.th}>Modelo resolvido</th>
                        <th style={s.th}>Tier</th>
                        <th style={s.th}>Canal</th>
                        <th style={s.thNum}>Invocações</th>
                        <th style={s.thNum}>Erros</th>
                        <th style={s.thNum}>Custo total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {modelos.map((m) => (
                        <tr key={`${m.resolved_model}|${m.tier}|${m.canal}`}>
                          <td style={{ ...s.td, wordBreak: 'break-all' }}>{m.resolved_model}</td>
                          <td style={s.td}>{m.tier}</td>
                          <td style={s.td}>{m.canal ? rotulo(m.canal) : '—'}</td>
                          <td style={s.tdNum}>{inteiro(m.invocacoes)}</td>
                          <td style={{ ...s.tdNum, color: m.erros > 0 ? C.bad : C.text }}>{inteiro(m.erros)}</td>
                          <td style={s.tdNum}><Custo valor={m.custo_total_usd} visivel={visiveis} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
              <Nota testId="nota-cobertura">
                Todos os números desta aba cobrem o período inteiro{doProjeto}, calculados no servidor,
                com execução excluída fora.
              </Nota>
            </>
          )}
    </div>
  );
}

export default DetalheDoAgente;
